import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Image, Eye, EyeOff } from 'lucide-react';

const PortfolioTab = ({ authAxios }) => {
  const [data, setData] = useState({
    subtitle: 'Our Portfolio',
    headline: 'Properties We',
    headline_accent: 'Manage',
    description: '',
    properties: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await authAxios.get('/admin/sections/portfolio');
        if (res.data && res.data.properties?.length > 0) {
          setData(prev => ({ ...prev, ...res.data }));
        }
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/sections/portfolio', data);
      toast.success('Portfolio section updated!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const addProperty = () => {
    setData({ ...data, properties: [...data.properties, { title: '', type: '', image: '', visible: true }] });
  };

  const removeProperty = (i) => {
    setData({ ...data, properties: data.properties.filter((_, idx) => idx !== i) });
  };

  const updateProperty = (i, field, value) => {
    const properties = [...data.properties];
    properties[i] = { ...properties[i], [field]: value };
    setData({ ...data, properties });
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="portfolio-section-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <Image className="w-5 h-5 text-[#D4AF37]" />
          Portfolio Section
        </h2>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Subtitle</label>
            <input value={data.subtitle} onChange={e => setData({...data, subtitle: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline</label>
              <input value={data.headline} onChange={e => setData({...data, headline: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline Accent</label>
              <input value={data.headline_accent} onChange={e => setData({...data, headline_accent: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Description</label>
            <textarea value={data.description} onChange={e => setData({...data, description: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
          </div>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Properties ({data.properties.length})</h2>
        <div className="space-y-3">
          {data.properties.map((prop, i) => (
            <div key={i} className={`p-4 bg-[#0A0A0A] border rounded space-y-3 ${prop.visible === false ? 'border-white/5 opacity-60' : 'border-white/5'}`}>
              <div className="flex items-start gap-3">
                {prop.image && <img src={prop.image} alt="" className="w-20 h-16 object-cover rounded border border-white/10 flex-shrink-0" />}
                <div className="flex-1 space-y-2">
                  <input value={prop.title} onChange={e => updateProperty(i, 'title', e.target.value)} placeholder="Property title" className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                  <input value={prop.type} onChange={e => updateProperty(i, 'type', e.target.value)} placeholder="Type (e.g. Accommodation Solutions)" className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
                  <input value={prop.image} onChange={e => updateProperty(i, 'image', e.target.value)} placeholder="Image URL" className="w-full bg-[#111111] border border-white/10 px-3 py-2 font-mono text-xs focus:border-[#D4AF37] outline-none text-white/50" />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => updateProperty(i, 'visible', prop.visible === false ? true : false)}
                    data-testid={`toggle-property-visible-${i}`}
                    className={`p-2 transition-colors ${prop.visible === false ? 'text-white/30 hover:text-white/60' : 'text-[#D4AF37] hover:text-[#D4AF37]/80'}`}
                    title={prop.visible === false ? 'Show on website' : 'Hide from website'}
                  >
                    {prop.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button onClick={() => removeProperty(i)} className="p-2 text-red-400/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {prop.visible === false && <span className="font-mono text-[10px] text-white/30 bg-white/5 px-1.5 py-0.5 rounded inline-block">HIDDEN ON WEBSITE</span>}
            </div>
          ))}
          <button onClick={addProperty} data-testid="add-property" className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors font-mono text-xs w-full justify-center">
            <Plus className="w-4 h-4" />Add Property
          </button>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="save-portfolio-section" className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Portfolio'}
      </button>
    </div>
  );
};

export default PortfolioTab;
